import React, { useEffect, useRef, useState } from "react";
import Loading from "../../icons/Loading";
import Sidebar from "./Sidebar";
import ProductTile from "./ProductTile";
import {useInView} from "react-intersection-observer";

const SORT_DEFAULT = 'default';
const SORT_NAME_ASC = 'name_acs';
const SORT_NAME_DESC = 'name_desc';
const SORT_PRICE_ASC = 'price_acs';
const SORT_PRICE_DESC = 'price_desc';
const SORT_POPULARITY_ASC = 'popularity_acs';
const SORT_POPULARITY_DESC = 'popularity_desc';

const ProductIndex = (props) => {
  const {
    url,
    settings,
    breadcrumbs
  } = props;

  const [init, setInit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [infiniteLoading, setInfiniteLoading] = useState(false);
  const [sidebar, setSidebar] = useState(false);
  const [sort, setSort] = useState('');
  const [products, setProducts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [colors, setColors] = useState([]);
  const [lastColorSelected, setLastColorSelected] = useState([]);
  const [twoTones, setTwoTones] = useState([]);
  const [twoTonesEnabled, setTwoTonesEnabled] = useState([]);
  const [fibre, setFibre] = useState([]);
  const [ranges, setRanges] = useState([]);
  const [tags, setTags] = useState([]);
  const [styles, setStyles] = useState([]);
  const [codes, setCodes] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [weights, setWeights] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [yScroll, setYScroll] = useState(-1);
  const [signal, setSignal] = useState(null);

  const loadingRef = useRef();
  const {ref, inView, entry} = useInView();

  const fetchProducts = async (clearPagination) => {
    if (signal) {
      signal.abort();
    }

    const controller = new AbortController();
    const s = controller.signal;
    const page = clearPagination ? 0 : currentPage + 1;
    if (clearPagination) {
      setLoading(true);
      setProducts([]);
      setHasNextPage(false);
    } else {
      setInfiniteLoading(true);
    }
    setCurrentPage(page);
    setSignal(controller);

    const params = new URLSearchParams({
      sort: sort,
      activities: activities,
      colors: colors,
      two_tones: twoTones,
      fibre: fibre,
      ranges: ranges,
      tags: tags,
      styles: styles,
      codes: codes,
      sizes: sizes,
      weights: weights,
      lastColorSelected: lastColorSelected,
      start: page * 12,
      attr: settings.attr
    });

    try {
      const response = await fetch(`${url}?${params}`, {
        method: 'GET',
        signal: s
      });
      const data = await response.json();
      const newProducts = clearPagination ? [...data.products] : [...products, ...data.products];
      setProducts(newProducts);
      setHasNextPage(!data.is_last);
      setLoading(false);
      setInfiniteLoading(false);

    } catch (error) {
      console.error("Error fetching products:", error);
    }
  }

  const loadProducts = () => {
    try {
      fetchProducts(true);
    } catch (e) { }
  }

  const loadNext = () => {
    try {
      fetchProducts(false);
    } catch (e) { }
  }

  const updatePushState = () => {
    let href = window.location.pathname;

    const params = {
      a: activities.length > 0 ? filterToURLParam(settings.activities, activities).join('|') : null,
      c: colors.length > 0 ? filterToURLParam(settings.colors, colors).join('|') : null,
      tt: twoTones.length > 0 ? filterToURLParam(settings.two_tones, twoTones).join('|') : null,
      f: fibre.length > 0 ? filterToURLParam(settings.fibre, fibre).join('|') : null,
      r: ranges.length > 0 ? filterToURLParam(settings.ranges, ranges).join('|') : null,
      t: tags.length > 0 ? filterToURLParam(settings.tags, tags).join('|') : null,
      s: sizes.length > 0 ? filterToURLParam(settings.sizes, sizes).join('|') : null,
      st: styles.length > 0 ? filterToURLParam(settings.styles, styles).join('|') : null,
      w: weights.length > 0 ? filterToURLParam(settings.weights, weights).join('|') : null,
      sc: codes.length > 0 ? filterToURLParam(settings.codes, codes).join('|') : null,
    };

    const paramString = Object.entries(params)
      .map(([key, value]) => {
        return value ? `${key}=${value}` : null
      }).filter((v) => !!v)
      .join('&');

    window.history.pushState(
      {},
      '',
      `${href}?${paramString}`
    );
  }

  const filterToURLParam = (haystack, collection) => {
    return haystack.filter((hI) => {
      return collection.indexOf(hI.id) >= 0;
    }).map((fI) => fI.title);
  }

  const filterFromURLParam = (haystack, collection) => {
    return collection.filter((cI) => {
      return haystack.indexOf(cI.title) >= 0;
    }).map((fI) => parseInt(fI.id));
  }

  const readURLParams = () => {
    const ret = {
      activities: [],
      colors: [],
      twoTones: [],
      fibre: [],
      ranges: [],
      tags: [],
      sizes: [],
      styles: [],
      codes: [],
      weights: [],
      twoTonesEnabled: false,
    };
    const query = window.location.search;
    if (query) {
      const urlParams = new URLSearchParams(query);
      const a = urlParams.get('a')
      if (a) {
        ret.activities = filterFromURLParam(a.split('|'), settings.activities);
      }
      const c = urlParams.get('c')
      if (c) {
        ret.colors = filterFromURLParam(c.split('|'), settings.colors);
      }
      const tt = urlParams.get('tt')
      if (tt) {
        ret.twoTones = filterFromURLParam(tt.split('|'), settings.two_tones);
        ret.twoTonesEnabled = true;
      }
      const f = urlParams.get('f')
      if (f) {
        ret.fibre = filterFromURLParam(f.split('|'), settings.fibre);
      }
      const r = urlParams.get('r')
      if (r) {
        ret.ranges = filterFromURLParam(r.split('|'), settings.ranges);
      }
      const t = urlParams.get('t')
      if (t) {
        ret.tags = filterFromURLParam(t.split('|'), settings.tags);
      }
      const s = urlParams.get('s')
      if (s) {
        ret.sizes = filterFromURLParam(s.split('|'), settings.sizes);
      }
      const st = urlParams.get('st')
      if (st) {
        ret.styles = filterFromURLParam(st.split('|'), settings.styles);
      }
      const sc = urlParams.get('sc')
      if (sc) {
        ret.codes = filterFromURLParam(sc.split('|'), settings.codes);
      }
      const w = urlParams.get('w')
      if (w) {
        ret.weights = filterFromURLParam(w.split('|'), settings.weights);
      }
    }
    return ret;
  }

  const openFilters = () => {
    setSidebar(true);
  }

  const closeFilters = () => {
    setSidebar(false);
  }

  const handlePopState = () => {
    const params = readURLParams();
    if (params.activities) { setActivities(params.activities) }

    setColors(params.colors);
    setTwoTones(params.twoTones);
    setTwoTonesEnabled(params.twoTonesEnabled);
    setFibre(params.fibre);
    setRanges(params.ranges);
    setTags(params.tags);
    setStyles(params.styles);
    setCodes(params.codes);
    setSizes(params.sizes);
    setWeights(params.weights);
    loadProducts();
  }

  const addRemoveFromCollection = (id, collection) => {
    const newCollection = [...collection];
    if (newCollection.indexOf(id) >= 0) {
      newCollection.splice(newCollection.indexOf(id), 1);
    } else {
      newCollection.push(id);
    }
    return newCollection;
  }

  const addRemoveActivity = (id) => {
    setActivities(addRemoveFromCollection(id, activities));
  }

  const addRemoveRange = (id) => {
    setRanges(addRemoveFromCollection(id, ranges));
  }

  const addRemoveFibre = (id) => {
    setFibre(addRemoveFromCollection(id, fibre));
  }

  const addRemoveTwoTone = (id) => {
    setTwoTones(addRemoveFromCollection(id, twoTones));
  }

  const addRemoveTags = (id) => {
    setTags(addRemoveFromCollection(id, tags));
  }

  const addRemoveStyles = (id) => {
    setStyles(addRemoveFromCollection(id, styles));
  }

  const addRemoveSizes = (id) => {
    setSizes(addRemoveFromCollection(id, sizes));
  }

  const addRemoveWeights = (id) => {
    setWeights(addRemoveFromCollection(id, weights));
  }

  const hasTwoToneSelected = () => {
    return twoTonesEnabled;
  }

  const addRemoveColor = (color) => {
    const id = color.id;
    if (id === 'two-tone') {
      setTwoTonesEnabled(!twoTonesEnabled);
      setTwoTones([]);
    } else {
      const newColors = [...colors];
      let added = false;
      if (newColors.indexOf(id) >= 0) {
        newColors.splice(newColors.indexOf(id), 1);
      } else {
        newColors.push(id);
        added = true;
      }

      setColors(newColors);
      if (added) {
        setLastColorSelected(color.urlSegment);
      }
    }
  }


  useEffect(() => {
    const params = readURLParams();
    setActivities(params.activities);
    //setColors(params.colors);
    setTwoTones(params.twoTones);
    setTwoTonesEnabled(params.twoTonesEnabled);
    setFibre(params.fibre);
    setRanges(params.ranges);
    setTags(params.tags);
    setStyles(params.styles);
    setCodes(params.codes);
    setSizes(params.sizes);
    setWeights(params.weights);
    setInit(true);

    if (params.colors && params.colors.length > 0) {
      setColors(params.colors);

      const lastColor = settings.colors.find(c => c.id === params.colors[params.colors.length - 1]);
      if (lastColor) {
        setLastColorSelected(lastColor.urlSegment);
      }
    }

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    }
  }, []);


  useEffect(() => {
    if (!loading && !infiniteLoading && hasNextPage) {
      loadNext();
    }
  }, [inView])


  useEffect(() => {
    updatePushState();
    if (init) {
      loadProducts();
    }
  }, [activities, colors, twoTones, fibre, ranges, tags, styles, codes, sizes, weights, sort, init]);


  return (
    <div className="product-index">
      <div className="container">
        {!settings.hero && (
          <div className="product-index__header">
            <h1 className="h3 product-index__breadcrumbs "
                dangerouslySetInnerHTML={{ __html: breadcrumbs }}>
            </h1>
            <div className="product-index__sort">
                            <span className="product-index__filter-toggle"
                                  onClick={openFilters}>Filter</span>
              <div className="dropdown-wrapper">
                <select onChange={(e) => { setSort(e.target.value) }}>
                  <option value={SORT_DEFAULT}>Default</option>
                  <option value={SORT_PRICE_ASC}>Price low to high</option>
                  <option value={SORT_PRICE_DESC}>Price high to low</option>
                  <option value={SORT_POPULARITY_ASC}>Popularity low to high</option>
                  <option value={SORT_POPULARITY_DESC}>Popularity high to low</option>
                  <option value={SORT_NAME_ASC}>A - Z</option>
                  <option value={SORT_NAME_DESC}>Z - A</option>
                </select>
              </div>
            </div>
            {!!settings.show_filters && <Sidebar
              addRemoveActivity={addRemoveActivity}
              addRemoveRange={addRemoveRange}
              addRemoveColor={addRemoveColor}
              addRemoveTwoTone={addRemoveTwoTone}
              addRemoveFibre={addRemoveFibre}
              addRemoveTags={addRemoveTags}
              addRemoveSizes={addRemoveSizes}
              addRemoveStyles={addRemoveStyles}
              addRemoveWeights={addRemoveWeights}
              hasTwoToneSelected={hasTwoToneSelected}
              closeFilters={closeFilters}
              settings={settings}
              opened={sidebar}
              selectedActivities={activities}
              colors={colors}
              tags={tags}
              activities={activities}
              fibres={fibre}
              ranges={ranges}
              twoTones={twoTones}
              styles={styles}
              sizes={sizes}
              weights={weights}
            />}

          </div>
        )}

        <div className="product-index__content">
          <div className="product-index__content__firstCol">
            <div className="filter-sidebar">
              <div className="product-index__sort__outer">
                <div className="product-index__sort product-index__sort--desktop ">
                                    <span className="product-index__filter-toggle"
                                          onClick={openFilters}>Filter</span>
                  <div className="dropdown-wrapper">
                    <select onChange={(e) => { setSort(e.target.value) }}>
                      <option value={SORT_DEFAULT}>Default</option>
                      <option value={SORT_PRICE_ASC}>Price low to high</option>
                      <option value={SORT_PRICE_DESC}>Price high to low</option>
                      <option value={SORT_POPULARITY_ASC}>Popularity low to high</option>
                      <option value={SORT_POPULARITY_DESC}>Popularity high to low</option>
                      <option value={SORT_NAME_ASC}>A - Z</option>
                      <option value={SORT_NAME_DESC}>Z - A</option>
                    </select>
                  </div>
                </div>
              </div>
              {!!settings.show_filters && <Sidebar
                addRemoveActivity={addRemoveActivity}
                addRemoveRange={addRemoveRange}
                addRemoveColor={addRemoveColor}
                addRemoveTwoTone={addRemoveTwoTone}
                addRemoveFibre={addRemoveFibre}
                addRemoveSizes={addRemoveSizes}
                addRemoveStyles={addRemoveStyles}
                addRemoveWeights={addRemoveWeights}
                addRemoveTags={addRemoveTags}
                hasTwoToneSelected={hasTwoToneSelected}
                closeFilters={closeFilters}
                settings={settings}
                opened={sidebar}
                selectedActivities={activities}
                colors={colors}
                activities={activities}
                fibres={fibre}
                ranges={ranges}
                twoTones={twoTones}
                tags={tags}
                styles={styles}
                sizes={sizes}
                weights={weights}
              />}
            </div>
          </div>

          <div className="product-index__grid-layout">
            {!!settings.hero && (
              <div className="product-index__sort-full">
                                <span className="product-index__filter-toggle"
                                      onClick={openFilters}>Filter</span>
                <div className="dropdown-wrapper">
                  <select onChange={(e) => { setSort(e.target.value) }}>
                    <option value={SORT_DEFAULT}>Default</option>
                    <option value={SORT_PRICE_ASC}>Price low to high</option>
                    <option value={SORT_PRICE_DESC}>Price high to low</option>
                    <option value={SORT_POPULARITY_ASC}>Popularity low to high</option>
                    <option value={SORT_POPULARITY_DESC}>Popularity high to low</option>
                    <option value={SORT_NAME_ASC}>A - Z</option>
                    <option value={SORT_NAME_DESC}>Z - A</option>
                  </select>
                </div>
              </div>
            )}

            {products.length > 0 && (
              <div className="product-index__grid">
                {products.map((product, index) => {
                  return <ProductTile
                    product={product}
                    key={index} />
                })}
              </div>
            )}

            {!!loading && <Loading />}

            {(products.length === 0 && !loading) && <p className="product-index__noproducts" >No items found.</p>}

            <div className="infinite-loader" ref={ref}>
              {!!infiniteLoading && !loading && <Loading />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductIndex;
